import {
  createContext,
  useContext
} from "react";
import { useSessionStorage } from "react-use";

import { useMsal } from '@azure/msal-react';
import * as IXrmTypes from "model/CRM";
import { ISecurityRole, ITeam } from "model/CRM.type";
import { ERoles } from 'model/Role.type';
import { ETeams } from 'model/Team.type';
import { Constants } from "./Application/Constants";



export interface IPrincipal {
  who?: any;
  systemUser?: IXrmTypes.systemuser;
  roles: ISecurityRole[];
  teams: ITeam[];
  username?: string;
  displayName?: string;
  token?: string;
}

export type TRoles = {
  [key in ERoles]?: ISecurityRole;
}

export type TTeams = {
  [key in ETeams]?: ITeam;
}

export interface IAuthorization {
  teams: any;
  roles: TRoles;
  canView: boolean;
  canEdit: boolean;
  canUWRC: boolean;
}

const defaultValues = {
  isAuthenticated: false,
  authorizations: null as (IAuthorization | null),
  principal: null as (IPrincipal | null),
  login: (principal: IPrincipal) => { },
  loginRefresh: () => { },
  logout: (where?: "app+msal" | "app") => { },
};

export const AuthenticationContext = createContext(defaultValues);

export const AuthenticationProvider = ({ children }: { children: JSX.Element }) => {
  const [authentication, setAuthentication] = useSessionStorage(Constants.app.authorizationStorageKey, defaultValues);
  const { instance } = useMsal();

  const login = (principal: IPrincipal) => {

    const roles = principal.roles.reduce((prev, curr) => {
      prev[curr.name] = curr;
      return prev;
    }, {} as TRoles);

    const teams = principal.teams.reduce((prev, curr) => {
      prev[curr.name] = curr;
      return prev;
    }, {} as TTeams);

    const authorizations: IAuthorization = {
      roles: roles,
      teams: teams,
      canView: !!roles['BCBS - Power User'] || !!roles["BCBS - Group Configuration App (View Only)"],
      canEdit: !!roles['BCBS - Power User'] || !!roles["BCBS - Group Configuration App (Edit)"],
      canUWRC: !!roles['BCBS - Power User'] || !!teams["Group Library Security - Underwriting"],
    }

    const _authentication = {
      ...authentication,
      isAuthenticated: true,
      principal: principal,
      authorizations: authorizations
    };

    //TODO add environment variable for this or remove from production build
    console.log(`[appl] AuthorizationProvider.login`, _authentication);

    setAuthentication(_authentication);
  }

  const loginRefresh = () => {
    setAuthentication(defaultValues);
  }

  const logout = (where?: "app+msal" | "app") => {
    setAuthentication(defaultValues);

    if (where === "app+msal")
      instance.logout()
  };

  // // Test code for authentications:
  // if (authentication?.authorizations?.roles)
  //   delete authentication.authorizations.roles["System Administrator"];
  // if (authentication?.authorizations?.roles)
  //   authentication.authorizations.roles['BCBS - PSA'] = { name: ERoles["BCBS - PSA"], roleid: '' }


  // after a context run, substitute defaults with right actions/values
  const authenticationValue = { ...authentication, login, loginRefresh, logout };

  return (
    <AuthenticationContext.Provider value={authenticationValue}>
      {children}
    </AuthenticationContext.Provider>
  );
}

export function useAuthentication() {
  return useContext(AuthenticationContext);
}
